import {
  cloneElement, forwardRef, isValidElement,
  type ButtonHTMLAttributes, type HTMLAttributes, type ReactElement, type ReactNode,
} from 'react'
import { ChevronRight } from 'lucide-react'
import { cx } from '../controls/cx'
import s from './NavigationSectionHead.module.css'

/**
 * THE RAIL'S SECTION HEAD — the Navbar "Chisel Line" sheet (#2406, LOCKED).
 *
 * ── why this is in the kit ─────────────────────────────────────────────────
 * The sheet's section head — a 15px section glyph at the left, the ENGRAVED
 * name (9.5px / .13em, uppercase, 800, a lit lip under the letters) and ONE
 * chevron at the right that rotates 90° on open — was drawn by the console's
 * `Nav.module.css .mod`, and the rogue census counted it as an app-styled
 * control on every cell of `/` (26 buttons x 4 cells). Its baseline reason said
 * why it could not simply become a `Button`: a raised block is the wrong
 * material for an engraved label, and no kit primitive drew a rail section
 * head. So the primitive is here now, drawing the SAME anatomy with the SAME
 * values — the sheet is locked; this moved it, it did not redesign it.
 *
 * ── what it must not know ──────────────────────────────────────────────────
 * Like `NavigationShell`, it fetches nothing and routes nothing. The glyph is
 * a node the host chose (the console's section marks are governed data), the
 * label is the section's name, and the three states arrive as props:
 *
 * - `open` — the section's list is disclosed: the label deepens to the ink,
 *   the glyph takes the brand text ink, the chevron turns 90°.
 * - `collapsed` — the rail is the 60px wall of marks: the head is its glyph
 *   alone, centred, at 17px; the name becomes the host's `aria-label`.
 * - `current` — on the wall, the section that owns the page keeps its glyph in
 *   the brand ink (the host's group draws the vein beside it).
 *
 * The states are the head's OWN attributes (`data-rail-*`), not a parent's:
 * the console's `.group[data-open]` and `[data-current]` are hooks its specs
 * locate groups by, and a second element carrying the same attribute would
 * make those locators ambiguous.
 *
 * ── the glyph ──────────────────────────────────────────────────────────────
 * The icon element receives the head's icon class (it must forward
 * `className`, as lucide icons and the kit's `EntityIcon` do). It stays the
 * head's FIRST child and an `<svg>` where the host passes one — hosts and
 * specs read the mark as `button > svg` and the name as `button > span`.
 */
export interface NavigationSectionHeadProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'> {
  /** The section's glyph. Decorative: the name (or, collapsed, the host's
   *  `aria-label`) is what names the control. */
  icon: ReactNode
  /** The section's name, engraved. */
  label: ReactNode
  /** The section's list is disclosed. */
  open: boolean
  /** The rail is collapsed to its wall of marks. */
  collapsed?: boolean
  /** On the collapsed wall, this section owns the current page. */
  current?: boolean
}

function withIconClass(icon: ReactNode): ReactNode {
  if (isValidElement(icon)) {
    const element = icon as ReactElement<{ className?: string }>
    return cloneElement(element, { className: cx(s.icon, element.props.className) })
  }
  return icon == null ? null : <span className={s.icon} aria-hidden="true">{icon}</span>
}

export const NavigationSectionHead = forwardRef<HTMLButtonElement, NavigationSectionHeadProps>(
  function NavigationSectionHead({ icon, label, open, collapsed, current, className, ...rest }, ref) {
    return (
      <button ref={ref} type="button" {...rest} className={cx(s.head, className)}
        /* The kit's own handle on "this is a section's head" (#2624): the
           collapsed flyout returns focus here on Escape. It used to find the
           head only by the console's `data-testid`, so a host outside the
           console that named its heads differently lost focus on dismissal. */
        data-rail-head=""
        data-rail-open={open ? '' : undefined}
        data-rail-collapsed={collapsed ? '' : undefined}
        data-rail-current={collapsed && current ? '' : undefined}>
        {withIconClass(icon)}
        <span className={s.label}>{label}</span>
        <ChevronRight aria-hidden className={s.chevron} />
      </button>
    )
  },
)

/**
 * The same section head when it is NOT the disclosure: a row that carries
 * other controls (the navigation editor's grip, name and row menu) or a plain
 * caption, drawn with the head's engraved type, inset and states so an edited
 * rail stays a faithful preview of the read rail it edits.
 */
export interface NavigationSectionHeadRowProps extends HTMLAttributes<HTMLDivElement> {
  /** The section's list is disclosed. */
  open?: boolean
}

export const NavigationSectionHeadRow = forwardRef<HTMLDivElement, NavigationSectionHeadRowProps>(
  function NavigationSectionHeadRow({ open, className, children, ...rest }, ref) {
    return (
      <div ref={ref} {...rest} className={cx(s.head, className)} data-rail-open={open ? '' : undefined}>
        {children}
      </div>
    )
  },
)
